import { Suspense, lazy, useEffect, useRef } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import "./App.css";

// Eagerly load ALL CSS from the project to match CRA's global CSS behavior.
// JS remains code-split via React.lazy; only CSS is loaded upfront.
import.meta.glob([
  "./pages/**/*.css",
  "./components/**/*.css",
], { eager: true });
import {
  signOut,
  scheduleTokenRenewal,
  fetchUserCredits,
} from "./redux/actions/authActions";
import { getUserRole, ROLE_IDS } from "./constants/roleConfig";
import { fetchUserCountry } from "./redux/actions/locationAction";
import RoleBasedRoutes from "./routes/RoleBasedRoutes";
import ScrollToTop from "./utils/ScrollToTop";
import { loadRuntimeFlags } from "./utils/runtimeFlags";
import { shouldDeferUserCreditsFetch } from "./utils/creditsFetchPath";
import { setJobPageFilters } from "./redux/reducers/jobReducer";
import { fetchCurrencies } from "./redux/actions/jobActions";
import logEnhancedError from "./utils/enhancedErrorLogger";
import { ErrorBoundary } from "react-error-boundary";
import SomethingWentWrong from "./components/CommonComponents/SomethingWentWrong";
import { loadSubscriptions } from "./redux/actions/subscriptionActions";
import { setupLinkSeedListener } from "./redux/store";
import { usePaymentReturnStatus } from "./hooks/usePaymentReturnStatus";

// Lazy: pulls in react-bootstrap, popper and the TeleCMI/Piopiy call UI, which only
// the recruiter dashboard needs. It renders nothing until a call starts.
const GlobalCallManager = lazy(() =>
  import("./components/CommonComponents/GlobalCallManager")
);

// Constants
const MEDIA_CACHE_CLEAR_MS = 5 * 60 * 1000; // 5 minutes
const CREDITS_STALE_MS = 30 * 1000; // refetch credits on nav if last fetch was >30s ago


// Non-blocking error logging using sendBeacon or setTimeout
const logErrorNonBlocking = (error, pathname) => {
  // Use sendBeacon if available for non-blocking logging
  if (navigator.sendBeacon) {
    try {
      const errorData = JSON.stringify({
        message: error.message,
        stack: error.stack,
        pathname,
        timestamp: new Date().toISOString(),
      });
      navigator.sendBeacon('/api/errors', errorData);
    } catch (beaconError) {
      // Fallback to setTimeout if sendBeacon fails
      setTimeout(() => {
        logEnhancedError(error, undefined, pathname).catch(() => {
          // Silently fail - error logging should not break the app
        });
      }, 0);
    }
  } else {
    // Fallback to setTimeout for non-blocking async logging
    setTimeout(() => {
      logEnhancedError(error, undefined, pathname).catch(() => {
        // Silently fail - error logging should not break the app
      });
    }, 0);
  }
};

// Global error handling setup
const setupGlobalErrorHandling = () => {
  // Handle unhandled promise rejections
  const handleUnhandledRejection = (event) => {
    // Handle AbortError specifically (interrupted play/pause operations)
    if (event.reason && event.reason.name === "AbortError") {
      event.preventDefault(); // Prevent the error from being logged
      return;
    }

    // Convert [object Object] to meaningful error message
    let errorReason = event.reason;
    if (errorReason && typeof errorReason === "object") {
      try {
        // Extract meaningful properties from the error object
        const errorProperties = Object.entries(errorReason)
          .filter(([key]) => !["stack", "name"].includes(key))
          .reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
          }, {});

        errorReason = {
          ...errorReason,
          message:
            errorReason.message ||
            `Error Details: ${JSON.stringify(errorProperties)}`,
        };
      } catch (e) {
        errorReason = new Error("Unknown error occurred");
      }
    }

    // Add memory usage information
    const memoryInfo = window.performance?.memory
      ? {
          usedJSHeapSize: window.performance.memory.usedJSHeapSize,
          totalJSHeapSize: window.performance.memory.totalJSHeapSize,
          jsHeapSizeLimit: window.performance.memory.jsHeapSizeLimit,
        }
      : undefined;
    // Handle ReferenceError: e is not defined (common in MediaRecorder events)
    if (
      event.reason &&
      event.reason.name === "ReferenceError" &&
      event.reason.message &&
      event.reason.message.includes("e is not defined")
    ) {
      console.warn(
        "ReferenceError: e is not defined (likely MediaRecorder event issue):",
        event.reason
      );
      event.preventDefault(); // Prevent the error from being logged
      return;
    }

    // Handle INTERNAL ASSERTION FAILED errors
    if (
      event.reason &&
      event.reason.message &&
      event.reason.message.includes("INTERNAL ASSERTION FAILED")
    ) {
      event.preventDefault(); // Prevent the error from being logged
      return;
    }

    // Handle WebSocket related errors - don't log these globally
    if (
      event.reason &&
      (event.reason.message?.includes("WebSocket") ||
        event.reason.message?.includes("Pending promise"))
    ) {
      console.error("WebSocket related error:", event.reason);
      event.preventDefault(); // Prevent the error from being logged
      return;
    }

    console.error("Unhandled promise rejection:", errorReason);

    // Only log if this is not a React error boundary error
    const isReactError =
      errorReason?.message?.includes("React") ||
      errorReason?.stack?.includes("ErrorBoundary");

    // Prevent recursive error logging
    const isLoggingError =
      errorReason?.message?.includes("Enhanced Error Report") ||
      errorReason?.message?.includes("Failed to send enhanced error log") ||
      errorReason?.stack?.includes("logEnhancedError");

    if (!isReactError && !isLoggingError) {
      // Create an enhanced error object with memory info
      const enhancedError = new Error(
        typeof errorReason === "string"
          ? errorReason
          : errorReason?.message || "Unknown error"
      );

      // Add additional context
      Object.assign(enhancedError, {
        originalError: errorReason,
        memoryInfo,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      });

      // Use non-blocking error logging
      logErrorNonBlocking(enhancedError, window.location.pathname);
    }
  };

  // Handle global errors
  const handleGlobalError = (event) => {
    // Handle AbortError specifically (interrupted play/pause operations)
    if (event.error && event.error.name === "AbortError") {
      console.error("Aborted operation:", event.error);
      return; // Don't log AbortError
    }

    // Handle ReferenceError: e is not defined (common in MediaRecorder events)
    if (
      event.error &&
      event.error.name === "ReferenceError" &&
      event.error.message &&
      event.error.message.includes("e is not defined")
    ) {
      console.warn(
        "ReferenceError: e is not defined (likely MediaRecorder event issue):",
        event.error
      );
      return; // Don't log this error
    }

    // Handle INTERNAL ASSERTION FAILED errors
    if (
      event.error &&
      event.error.message &&
      event.error.message.includes("INTERNAL ASSERTION FAILED")
    ) {
      console.error(
        "Internal assertion failed (likely WebSocket related):",
        event.error
      );
      return; // Don't log this error
    }

    // Handle WebSocket related errors - don't log these globally
    if (
      event.error &&
      (event.error.message?.includes("WebSocket") ||
        event.error.message?.includes("Pending promise"))
    ) {
      console.error("WebSocket related error:", event.error);
      return; // Don't log this error
    }

    console.error("Global error:", event.error);

    // Only log if this is not a React error boundary error
    const isReactError =
      event.error?.message?.includes("React") ||
      event.error?.stack?.includes("ErrorBoundary") ||
      event.message?.includes("React");

    // Prevent recursive error logging
    const isLoggingError =
      event.error?.message?.includes("Enhanced Error Report") ||
      event.error?.message?.includes("Failed to send enhanced error log") ||
      event.error?.stack?.includes("logEnhancedError") ||
      event.message?.includes("Enhanced Error Report");

    if (!isReactError && !isLoggingError) {
      // Use non-blocking error logging
      logErrorNonBlocking(
        new Error(`Global Error: ${event.error?.message || event.message}`),
        window.location.pathname
      );
    }
  };

  // Add event listeners
  window.addEventListener("unhandledrejection", handleUnhandledRejection);
  window.addEventListener("error", handleGlobalError);

  // Memory management - periodically clear caches and force GC
  const memoryCleanupInterval = setInterval(() => {
    try {
      // Clear media caches every 5 minutes
      if ("caches" in window) {
        caches.keys().then((cacheNames) => {
          cacheNames.forEach((cacheName) => {
            if (
              cacheName.includes("media") ||
              cacheName.includes("audio") ||
              cacheName.includes("video")
            ) {
              caches.delete(cacheName);
            }
          });
        });
      }

      // Force garbage collection if available
      if (window.gc) {
        window.gc();
      }
    } catch (error) {
      // Ignore memory management errors
    }
  }, MEDIA_CACHE_CLEAR_MS);

  // Return cleanup function
  return () => {
    window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    window.removeEventListener("error", handleGlobalError);
    clearInterval(memoryCleanupInterval);
  };
};

// Load /djadmin runtime switches early (mobile interview gate reads them synchronously).
loadRuntimeFlags();

function App() {
  const userLoggedIn = useSelector((state) => !!state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.user);
  const role = getUserRole(user);
  const dispatch = useDispatch();
  const location = useLocation();
  const userCredits = useSelector((state) => state.auth.userCredits);
  const prevPathRef = useRef(location.pathname);
  const lastCreditsFetchTime = useRef(0);

  // Global Stripe-return handler: fires the existing payment toast on whatever
  // page the backend redirects to via the checkout `return_path`, then cleans
  // the status params from the URL.
  usePaymentReturnStatus();

  const onIdle = () => {
    dispatch(signOut());
  };

  const onActive = () => {
    // Use sessionStorage for tab-specific idle tracking
    sessionStorage.setItem("lastActiveTime", Date.now());
  };

  useIdleTimer({
    onIdle,
    onActive,
    timeout: 86400000, //24 hours
    throttle: 0,
  });

  // Auth-scope initialization: runs when the user logs in/out or their
  // token/role changes. Does NOT depend on location so it does not re-run
  // on every navigation (which was causing unnecessary fetches).
  useEffect(() => {
    if (userLoggedIn) {
      dispatch(fetchCurrencies(token));

      // Schedule token renewal once per login session.
      const persistedState = sessionStorage.getItem("persist:root");
      if (persistedState) {
        try {
          const parsedState = JSON.parse(persistedState);
          const authState = JSON.parse(parsedState.auth || "{}");
          const userId = authState.user?.user_id || authState.userId;
          if (userId && token) {
            scheduleTokenRenewal(userId, dispatch);
          }
        } catch (e) {
          console.error("Error initializing token renewal:", e);
        }
      }
    }
    dispatch(fetchUserCountry());
  }, [dispatch, userLoggedIn, token, role]);

  // Credits: fetch when no cache, leaving a deferred path, or nav after stale window.
  // Background refreshes skip the loading placeholder (see fetchUserCredits).
  useEffect(() => {
    const prevPath = prevPathRef.current;
    prevPathRef.current = location.pathname;

    if (
      !userLoggedIn ||
      !token ||
      role == null ||
      role === ROLE_IDS.APPLICANT ||
      shouldDeferUserCreditsFetch(location.pathname)
    ) {
      return;
    }

    const comingFromDeferredPath = shouldDeferUserCreditsFetch(prevPath);
    const isStale =
      Date.now() - lastCreditsFetchTime.current > CREDITS_STALE_MS;

    if (userCredits == null || comingFromDeferredPath || isStale) {
      lastCreditsFetchTime.current = Date.now();
      dispatch(fetchUserCredits());
    }
  }, [dispatch, userLoggedIn, token, role, location.pathname]);

  useEffect(() => {
    // Use sessionStorage for tab-specific idle tracking
    const lastActiveTime = sessionStorage.getItem("lastActiveTime");
    if (lastActiveTime && Date.now() - parseInt(lastActiveTime) > 86400000) {
      // 24 hours
      dispatch(signOut());
    }
  }, [dispatch]);

  useEffect(() => {
    //on page reload reset Job Page filters
    dispatch(
      setJobPageFilters({
        current: 1,
        sortBy: "All",
        status: "All",
        searchInput: "",
        type: "All",
      })
    );

    dispatch(loadSubscriptions());

    // Setup global error handling and get cleanup function
    const cleanupErrors = setupGlobalErrorHandling();

    // Install link-seed listener (snapshots session on link right-click
    // so new tabs opened from this tab inherit the correct session)
    const cleanupLinkSeed = setupLinkSeedListener();

    return () => {
      cleanupErrors();
      cleanupLinkSeed();
    };
  }, [dispatch]);

  function ErrorFallback({ error, resetErrorBoundary }) {
    // Only log if this is not a child error boundary error
    const isChildError =
      error.stack?.includes("ErrorBoundary") ||
      error.message?.includes("ErrorBoundary");

    // Prevent recursive error logging by checking if the error is from logEnhancedError itself
    const isLoggingError =
      error.message?.includes("Enhanced Error Report") ||
      error.message?.includes("Failed to send enhanced error log") ||
      error.stack?.includes("logEnhancedError");

    if (!isChildError && !isLoggingError) {
      // Wrap in try-catch to prevent recursive errors
      try {
        logEnhancedError(error, undefined, location.pathname);
      } catch (logError) {
        console.error("Failed to log error:", logError);
      }
    }

    return <SomethingWentWrong resetErrorBoundary={resetErrorBoundary} />;
  }

  // TeleCMI/Piopiy is recruiter-only: mount GlobalCallManager only on recruiter dashboard
  const isRecruiterDashboard = location.pathname.startsWith("/dashboard");

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        window.location.reload();
      }}
    >
      <ScrollToTop />
      {isRecruiterDashboard && (
        <Suspense fallback={null}>
          <GlobalCallManager />
        </Suspense>
      )}
      <RoleBasedRoutes />
    </ErrorBoundary>
  );
}

export default App;
